import { notification } from "@excent-one/react/notification";
import { BadRequestError } from "@excent-one/core";
import { capitalCase } from "text-case";
import {
  ControlledDataGridColumnProps,
  FormFor,
  ControlledDataGrid,
  ControlledInput,
  ControlledDatePicker,
  ControlledTimePicker,
  ControlledComboBox,
  FormSection,
} from "@excent-one/react/form";
import { useNavigate, useParams } from "@tanstack/react-router";
import { useState, useEffect, useCallback, useMemo } from "react";
import { useController, UseFormSetValue, useWatch } from "react-hook-form";
import {
  useQuery,
  useQueries,
  useQueryClient,
  QueryClient,
} from "@tanstack/react-query";
import { Button, FormFieldSet } from "@progress/kendo-react-all";
import { useAuthContext } from "@excent-one/auth";
import {
  ContentLayout,
  ContentHeader,
  ContentHeaderBackNavigation,
  ContentHeaderTitle,
  ContentHeaderBreadcrumbs,
} from "@excent-one/react/layout/content";
import { dialogBox } from "@excent-one/react/dialog-box";
import { saveIcon, cancelIcon, pencilIcon } from "@progress/kendo-svg-icons";
import { useRouteHandle } from "@excent-one/react/routing/useRouteHandle";
import FormActionsBar from "@excent-one/react/form/layouts/FormActionsBar";
import {
  getAllBranches,
  getAllConcernsIssueTypes,
  getAllConcernStatuses,
  getDailyActivityMonitoringLogFiles,
  getDailyActivityMonitoringLogHeaderById,
  saveDailyActivityMonitoringLogHeader,
  submitDailyActivityMonitoringLogHeader,
  completeDailyActivityMonitoringLogHeader,
  uploadFileDailyActivityMonitoringLogHeaders,
  deleteDailyActivityMonitoringLogFile,
  cancelDailyActivityMonitoringLogHeader,
  ConcernsIssueDto,
  getDailyActivityMonitoringLogConcernsIssues,
} from "@phrmpc/infrastructure/phrmpc";
import {
  type DailyActivityMonitoringLogHeaderDto,
  type DailyActivityMonitoringLogDetailDto,
} from "@phrmpc/infrastructure/phrmpc";
import { PARTICULAR_OPTIONS } from "@phrmpc/shared";
import { formatDate } from "@progress/kendo-intl";
import { DateTime } from "luxon";
import ConcernsManagerWindow from "@phrmpc/shared/components/ConcernsManagerWindow";
import { resolveDamlAttachmentUrl } from "@phrmpc/shared/hooks/useDamlAttachmentContent";
import type { FileDto } from "@phrmpc/infrastructure/phrmpc/models";

const DAML_SIGNATURE_ALLOWED_EXTENSIONS = [".jpg", ".jpeg", ".png"];
// Matches the removed ControlledUpload's own DEFAULT_MAX_FILE_SIZE - this call site never passed
// an explicit maxFileSize before, so it relied on that 20MB default; the library's own restriction
// has no default (an omitted maxFileSize means unbounded), so it must be set explicitly here to
// avoid silently lifting the size cap.
const DAML_SIGNATURE_MAX_FILE_SIZE = 20 * 1024 * 1024;

interface ActivityRow extends Omit<DailyActivityMonitoringLogDetailDto, "id"> {
  id: string;
  concern: ConcernsIssueDto[];
  signature: FileDto | null;
}

interface DailyActivityMonitoringLogFormValues extends Omit<
  DailyActivityMonitoringLogHeaderDto,
  "date" | "time" | "dailyActivityMonitoringLogDetails"
> {
  date: Date;
  time: Date;
  dailyActivityMonitoringLogDetails: ActivityRow[];
}

const now = new Date();

const defaultValues: DailyActivityMonitoringLogFormValues = {
  id: undefined,
  logId: "",
  coordinatorId: undefined,
  coordinatorName: "",
  dailyWorkSchedule: "",
  date: now,
  time: now,
  branchId: undefined,
  statusId: undefined,
  statusName: "",
  dailyActivityMonitoringLogDetails: [],
};

const { Form, useFormRef } = FormFor<DailyActivityMonitoringLogFormValues>();

const timeOnlyToDate = (raw: any): Date => {
  const d = new Date();
  if (typeof raw === "string") {
    const [hourStr, minuteStr, secondStr] = raw.split(":");
    d.setHours(
      Number(hourStr) || 0,
      Number(minuteStr) || 0,
      parseInt(secondStr, 10) || 0,
      0,
    );
    return d;
  }
  d.setHours(raw?.hour ?? 0, raw?.minute ?? 0, raw?.second ?? 0, 0);
  return d;
};

const dateToTimeOnlyString = (d: Date) =>
  DateTime.fromJSDate(d).toFormat("HH:mm:ss");

const getServerErrorMessage = (e: unknown, fallback: string): string => {
  if (e instanceof BadRequestError) {
    const errors = e.data?.errors as Record<string, string[]> | undefined;
    const messages = errors ? Object.values(errors).flat() : [];
    if (messages.length > 0) return messages.join(" ");
    if (e.data?.title) return e.data.title;
  }
  return fallback;
};

interface ConcernCellProps {
  control: any;
  setValue: UseFormSetValue<DailyActivityMonitoringLogFormValues>;
  name: `dailyActivityMonitoringLogDetails.${number}.concern`;
  readOnly: boolean;
  dailyActivityMonitoringId: string;
  memberId: string;
  memberName: string;
}

const ConcernCell = ({
  control,
  setValue,
  name,
  readOnly,
  dailyActivityMonitoringId,
  memberId,
  memberName,
}: ConcernCellProps) => {
  const { field } = useController({
    control,
    name,
  });
  const [open, setOpen] = useState(false);

  const { data: concernStatusOptions = [] } = useQuery({
    queryKey: ["concern-statuses"],
    queryFn: () => getAllConcernStatuses(),
  });

  const { data: concernsIssueTypeOptions = [] } = useQuery({
    queryKey: ["concerns-issue-types"],
    queryFn: () => getAllConcernsIssueTypes(),
  });

  const { data: existingConcerns } = useQuery({
    queryKey: ["daml-concerns", dailyActivityMonitoringId],
    queryFn: () =>
      getDailyActivityMonitoringLogConcernsIssues(dailyActivityMonitoringId),
    enabled: Boolean(dailyActivityMonitoringId),
  });

  // Pre-existing bug (confirmed live, reproduces even without any upload-migration changes):
  // this previously guarded seeding with a mount-scoped `useRef` instead of comparing values, so
  // any remount of this cell (Kendo Grid's `cells.data` render function has no explicit `key`,
  // so React can remount it on unrelated grid re-renders) reset the ref and re-seeded the field
  // via `setValue` unconditionally whenever `existingConcerns` was already cached/resolved -
  // triggering a parent re-render, which could remount the cell again, sustaining "Maximum update
  // depth exceeded". Comparing against the field's current value instead is remount-safe: seeding
  // is idempotent regardless of how many times this effect (re)fires.
  useEffect(() => {
    if (!existingConcerns || concernStatusOptions.length === 0) return;
    if (existingConcerns.length === 0) return;

    const mapped: ConcernsIssueDto[] = existingConcerns.map((c) => ({
      ...c,
      description: c.description ?? "",
    }));
    const currentIds = (field.value ?? [])
      .map((c: ConcernsIssueDto) => c.id)
      .join(",");
    const targetIds = mapped.map((c) => c.id).join(",");
    if (currentIds === targetIds) return;
    setValue(name, mapped, { shouldDirty: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingConcerns, concernStatusOptions.length]);

  const concerns: ConcernsIssueDto[] = field.value ?? [];

  if (readOnly && concerns.length === 0) {
    return <span style={{ fontSize: 13, color: "#999" }}>No concerns</span>;
  }

  return (
    <>
      <Button type="button" fillMode="flat" onClick={() => setOpen(true)}>
        {readOnly
          ? `View (${concerns.length})`
          : concerns.length > 0
            ? `Manage (${concerns.length})`
            : "Add"}
      </Button>

      {open && (
        <ConcernsManagerWindow
          concerns={concerns}
          concernIssueOptions={concernsIssueTypeOptions}
          concernStatusOptions={concernStatusOptions}
          readOnly={readOnly}
          dailyActivityMonitoringId={dailyActivityMonitoringId}
          memberId={memberId}
          memberName={memberName}
          onClose={() => setOpen(false)}
          onSubmit={(updated) => setValue(name, updated, { shouldDirty: true })}
        />
      )}
    </>
  );
};

/** `editorProps` builder for the "signature" grid column's "upload" editor - uploads/deletes
 *  through the daml-file endpoints and resolves preview URLs lazily via `getFileUrl`.
 *
 * Binds with `multiple: false` to the row's scalar `signature` field (not an array) - this field
 * isn't part of the save payload (`buildPayload` never reads it; the record's signature file is
 * managed entirely through these dedicated endpoints), so there's no array-typed DTO contract to
 * satisfy, and `multiple: false` is what makes the library's own upload-completion logic
 * unconditionally replace rather than accumulate. `multiple: true` (array binding) was tried first
 * and confirmed live (via a Playwright test against the structurally identical Transmittal
 * attachment column) to accumulate instead of replace - the library always merges `onUpload`'s
 * return value into whatever the bound field held when its post-upload `onFilesChange` fires, and
 * no external `setValue`, immediate or via a resync effect, reliably beats that merge. */
const buildDamlSignatureEditorProps = ({
  dailyActivityMonitoringDetailId,
  readOnly,
  queryClient,
}: {
  dailyActivityMonitoringDetailId: string;
  readOnly: boolean;
  queryClient: QueryClient;
}) => ({
  multiple: false,
  permissionMode: readOnly
    ? ("read-only" as const)
    : ("upload-delete" as const),
  restrictions: {
    allowedExtensions: DAML_SIGNATURE_ALLOWED_EXTENSIONS,
    maxFileSize: DAML_SIGNATURE_MAX_FILE_SIZE,
  },
  getFileName: (file: FileDto) => file.fileName ?? "",
  getFileUrl: (file: FileDto) =>
    file.id
      ? resolveDamlAttachmentUrl(
          dailyActivityMonitoringDetailId,
          file.id,
          file.fileName ?? "",
        )
      : undefined,
  onUpload: async (rawFile: File) => {
    // This field has always been single-file/replace-on-reupload - delete any existing file(s) on
    // the server before uploading the new one so no orphaned attachment is left behind there (the
    // `multiple: false` binding above already ensures the *displayed* field replaces rather than
    // accumulates).
    const existingFiles = await getDailyActivityMonitoringLogFiles(
      dailyActivityMonitoringDetailId,
    );
    await Promise.all(
      (existingFiles ?? [])
        .filter((f) => f.id)
        .map((f) =>
          deleteDailyActivityMonitoringLogFile(
            dailyActivityMonitoringDetailId,
            f.id!,
          ),
        ),
    );
    await uploadFileDailyActivityMonitoringLogHeaders(
      dailyActivityMonitoringDetailId,
      { file: rawFile },
    );
    const updatedFiles = await getDailyActivityMonitoringLogFiles(
      dailyActivityMonitoringDetailId,
    );
    await queryClient.invalidateQueries({
      queryKey: ["daml-signature-files", dailyActivityMonitoringDetailId],
    });
    return (
      (updatedFiles ?? [])[(updatedFiles ?? []).length - 1] ?? {
        fileName: rawFile.name,
      }
    );
  },
  onDelete: async (file: FileDto) => {
    if (!file.id) return;
    await deleteDailyActivityMonitoringLogFile(
      dailyActivityMonitoringDetailId,
      file.id,
    );
    await queryClient.invalidateQueries({
      queryKey: ["daml-signature-files", dailyActivityMonitoringDetailId],
    });
  },
});

const DailyActivityMonitoringLogForm = () => {
  const { id } = useParams({ strict: false });
  const { action } = useRouteHandle();
  const navigate = useNavigate();
  const formRef = useFormRef();
  const [{ currentUser }] = useAuthContext();
  const queryClient = useQueryClient();

  const { data: branches } = useQuery({
    queryKey: ["branches"],
    queryFn: () => getAllBranches(),
  });

  const {
    data: headerData,
    isLoading: headerLoading,
    isFetching: headerFetching,
  } = useQuery({
    queryKey: ["daml-header", id],
    queryFn: () => getDailyActivityMonitoringLogHeaderById(id!),
    enabled: Boolean(id),
  });

  const loading = headerLoading || headerFetching;

  const isOwner =
    Boolean(currentUser?.id) && currentUser?.id === headerData?.createdBy;

  const readOnly = action === "display" || (action === "edit" && !isOwner);

  const mapDetailToRow = (
    d: DailyActivityMonitoringLogDetailDto,
  ): ActivityRow => ({
    id: d.id ?? crypto.randomUUID(),
    memberId: d.memberId ?? "",
    memberName: d.memberName ?? "",
    particularTypeId: d.particularTypeId ?? undefined,
    location: d.location ?? "",
    updatedContactNumber: d.updatedContactNumber ?? "",
    concern: [],
    signature: d.files?.[d.files.length - 1] ?? null,
  });

  const mapHeaderToFormValues = useCallback(
    (
      header: DailyActivityMonitoringLogHeaderDto,
    ): DailyActivityMonitoringLogFormValues => ({
      id: header.id,
      logId: header.logId ?? "",
      coordinatorId: header.coordinatorId,
      coordinatorName: header.coordinatorName || currentUser?.displayName || "",
      dailyWorkSchedule: header.dailyWorkSchedule ?? "",
      date: header.date ? new Date(header.date) : now,
      time: header.time ? timeOnlyToDate(header.time) : now,
      branchId: header.branchId,
      statusId: header.statusId,
      statusName: header.statusName ?? "",
      dailyActivityMonitoringLogDetails: (
        header.dailyActivityMonitoringLogDetails ?? []
      ).map(mapDetailToRow),
    }),
    [currentUser],
  );

  const initialValues = useMemo<DailyActivityMonitoringLogFormValues>(() => {
    if (!headerData) {
      return {
        ...defaultValues,
        coordinatorName: currentUser?.displayName ?? "",
      };
    }
    return mapHeaderToFormValues(headerData);
  }, [headerData, mapHeaderToFormValues, currentUser]);

  const buildPayload = useCallback(
    (
      values: DailyActivityMonitoringLogFormValues,
    ): DailyActivityMonitoringLogHeaderDto => ({
      id,
      logId: values.logId || undefined,
      coordinatorId: values.coordinatorId,
      coordinatorName: values.coordinatorName,
      dailyWorkSchedule: values.dailyWorkSchedule,
      date: values.date,
      time: dateToTimeOnlyString(values.time) as any,
      branchId: values.branchId,
      statusId: values.statusId,
      dailyActivityMonitoringLogDetails:
        values.dailyActivityMonitoringLogDetails.map((a) => ({
          id: a.id,
          memberId: a.memberId,
          memberName: a.memberName,
          particularTypeId: a.particularTypeId ?? undefined,
          location: a.location || null,
          updatedContactNumber: a.updatedContactNumber || null,
        })) as any,
    }),
    [id],
  );

  const submit = useCallback(
    async (
      values: DailyActivityMonitoringLogFormValues,
    ): Promise<DailyActivityMonitoringLogFormValues> => {
      try {
        const saved = await saveDailyActivityMonitoringLogHeader(id!, {
          id: id!,
          payload: buildPayload(values),
        });
        notification.success("Monitoring Log saved successfully.");
        queryClient.setQueryData(["daml-header", id], saved);
        await Promise.all(
          (saved.dailyActivityMonitoringLogDetails ?? [])
            .filter((d) => d.id)
            .map((d) =>
              queryClient.invalidateQueries({
                queryKey: ["daml-concerns", d.id],
              }),
            ),
        );

        const remapped = mapHeaderToFormValues(saved);
        remapped.dailyActivityMonitoringLogDetails =
          remapped.dailyActivityMonitoringLogDetails.map((row) => {
            const prev = values.dailyActivityMonitoringLogDetails.find(
              (p) => p.id === row.id,
            );
            return {
              ...row,
              concern: prev?.concern ?? row.concern,
              signature: prev?.signature ?? row.signature,
            };
          });
        return remapped;
      } catch (e) {
        console.error(e);
        notification.error(
          getServerErrorMessage(
            e,
            "Unable to save Monitoring Log. Please try again.",
          ),
        );
        throw e;
      }
    },
    [id, buildPayload, mapHeaderToFormValues, queryClient],
  );

  const handleSubmitFromEdit = useCallback(
    async (values: DailyActivityMonitoringLogFormValues) => {
      if (values.dailyActivityMonitoringLogDetails.length === 0) {
        notification.error("Please complete all mandatory fields to proceed.");
        return;
      }
      try {
        const submitted = await submitDailyActivityMonitoringLogHeader(id!, {
          id: id!,
          payload: buildPayload(values),
        });
        notification.success("Monitoring Log submitted successfully.");
        queryClient.setQueryData(["daml-header", id], submitted);
        await Promise.all(
          (submitted.dailyActivityMonitoringLogDetails ?? [])
            .filter((d) => d.id)
            .map((d) =>
              queryClient.invalidateQueries({
                queryKey: ["daml-concerns", d.id],
              }),
            ),
        );
        await queryClient.invalidateQueries({
          queryKey: ["daml-headers"],
        });
        navigate({
          to: "/deployment/daily-activity-monitoring-logs/$id/display",
          params: { id: id! },
        });
      } catch (e) {
        console.error(e);
        notification.error(
          getServerErrorMessage(e, "Unable to submit. Please try again."),
        );
      }
    },
    [id, buildPayload, queryClient, navigate],
  );

  const handleCancel = useCallback(
    (values: DailyActivityMonitoringLogFormValues) => {
      dialogBox.confirm({
        title: "Cancel Monitoring Log",
        content: <>Are you sure you want to cancel this Monitoring Log?</>,
        actions: {
          confirm: {
            text: "Yes",
            onClick: async () => {
              try {
                await cancelDailyActivityMonitoringLogHeader(id!, {
                  id: id!,
                  payload: buildPayload(values),
                });
                notification.success("Monitoring Log cancelled successfully.");
                await queryClient.invalidateQueries({
                  queryKey: ["daml-header", id],
                });
                await queryClient.invalidateQueries({
                  queryKey: ["daml-headers"],
                });
                navigate({ to: "/deployment/daily-activity-monitoring-logs" });
              } catch (error) {
                console.error(error);
                notification.error(
                  getServerErrorMessage(
                    error,
                    "Unable to cancel Monitoring Log. Please try again.",
                  ),
                );
              }
            },
          },
          cancel: { text: "No" },
        },
      });
    },
    [id, buildPayload, queryClient, navigate],
  );

  const goExit = useCallback(() => {
    if (!formRef.current) return;

    const proceed = () =>
      navigate({ to: "/deployment/daily-activity-monitoring-logs" });

    const hasUnsavedChanges =
      Object.keys(formRef.current.formState.dirtyFields).length > 0;

    if (hasUnsavedChanges) {
      dialogBox.confirm({
        title: "Leave without saving?",
        content: <>You have unsaved changes. Are you sure you want to leave?</>,
        actions: {
          confirm: { text: "Yes, leave", onClick: proceed },
          cancel: { text: "No" },
        },
      });
    } else {
      proceed();
    }
  }, [navigate]);

  const handleFinalize = useCallback(
    (values: DailyActivityMonitoringLogFormValues) => {
      dialogBox.confirm({
        title: "Finalize monitoring log",
        content: <>Are you sure you want to finalize this monitoring log?</>,
        actions: {
          confirm: {
            text: "Yes",
            onClick: async () => {
              try {
                await completeDailyActivityMonitoringLogHeader(id!, {
                  id: id!,
                  payload: buildPayload(values),
                });
                notification.success("Monitoring Log finalized.");
                await queryClient.invalidateQueries({
                  queryKey: ["daml-header", id],
                });
                await queryClient.invalidateQueries({
                  queryKey: ["daml-headers"],
                });
              } catch (e) {
                console.error(e);
                notification.error(
                  getServerErrorMessage(
                    e,
                    "Unable to finalize. Please try again.",
                  ),
                );
              }
            },
          },
          cancel: { text: "No" },
        },
      });
    },
    [id, buildPayload, queryClient],
  );

  return (
    <ContentLayout>
      <ContentHeader loading={loading}>
        <ContentHeaderBackNavigation />
        <ContentHeaderTitle
          title={`${capitalCase(action || "display")} Daily Activity Monitoring Log`}
        />
        <ContentHeaderBreadcrumbs />
      </ContentHeader>

      <Form
        ref={formRef}
        onSubmit={submit}
        values={initialValues}
        defaultValues={defaultValues}
        errorRoot="payload"
      >
        {({ control, setValue, getValues }) => {
          const statusName = useWatch({ control, name: "statusName" });
          const isDraft = statusName === "Draft";
          const isSubmitted = statusName === "Submitted";
          const dateTimeReadOnly = true;

          // The "signature" upload column binds to each row's scalar `signature` field, but
          // (unlike Transmittal's filesReleased) that field isn't part of the header DTO - the old
          // DamlSignatureCell fetched it per-row itself. Since the "upload" grid editor is static
          // config, not a component, seed each row's field here once its file resolves; ongoing
          // upload/delete changes are handled natively by the library itself (see
          // buildDamlSignatureEditorProps' own comment) - this effect only covers initial load.
          const detailRows = useWatch({
            control,
            name: "dailyActivityMonitoringLogDetails",
          });
          const signatureFileQueries = useQueries({
            queries: detailRows.map((row) => ({
              queryKey: ["daml-signature-files", row.id],
              queryFn: () => getDailyActivityMonitoringLogFiles(row.id),
              enabled: Boolean(row.id),
            })),
          });
          // `useWatch`'s array result and `useQueries`' return array are both new references every
          // render regardless of value - depending on them directly re-ran this effect on every
          // render, including the ones its own `setValue` calls caused, without ever converging
          // (confirmed live: "Maximum update depth exceeded", sustained). Depend on stable primitive
          // keys instead (row id list + each query's own `dataUpdatedAt`) so the effect only reruns
          // when a row's identity or a query's actually-fetched data genuinely changes.
          const detailRowIdsKey = detailRows.map((row) => row.id).join(",");
          const signatureDataUpdatedAtKey = signatureFileQueries
            .map((query) => query.dataUpdatedAt)
            .join(",");
          useEffect(() => {
            signatureFileQueries.forEach((query, index) => {
              const resolvedFiles = query.data;
              if (!resolvedFiles) return;
              const row = detailRows[index];
              const resolvedFile =
                resolvedFiles[resolvedFiles.length - 1] ?? null;
              if ((row?.signature?.id ?? null) !== (resolvedFile?.id ?? null)) {
                setValue(
                  `dailyActivityMonitoringLogDetails.${index}.signature`,
                  resolvedFile,
                  { shouldDirty: false },
                );
              }
            });
            // eslint-disable-next-line react-hooks/exhaustive-deps
          }, [detailRowIdsKey, signatureDataUpdatedAtKey, setValue]);

          const activityColumns: ControlledDataGridColumnProps<ActivityRow>[] =
            [
              {
                field: "memberName",
                title: "Name of Member",
                readOnly: true,
                width: "200px",
                locked: true,
              },
              {
                field: "particularTypeId",
                title: "Particulars/Details",
                width: "220px",
                editor: readOnly ? undefined : "combobox",
                editorProps: {
                  data: PARTICULAR_OPTIONS,
                  dataItemKey: "id",
                  textField: "name",
                  placeholder: "Select particular...",
                },
                display: ({ value }: any) => (
                  <>
                    {PARTICULAR_OPTIONS.find((p) => p.id === value)?.name ??
                      "-"}
                  </>
                ),
              },
              {
                field: "location",
                title: "Location",
                editor: readOnly ? undefined : "text",
                width: "180px",
              },
              {
                field: "updatedContactNumber",
                title: "Updated Contact Number",
                editor: readOnly ? undefined : "text",
                readOnly,
                width: "200px",
                editorProps: {
                  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                    if (e.key.length > 1) return;
                    if (!/^[0-9]$/.test(e.key)) {
                      e.preventDefault();
                    }
                  },
                },
              },
              {
                field: "concern",
                title: "Remarks / Concern",
                width: "160px",
                cells: {
                  data: (props: any) => (
                    <td>
                      <ConcernCell
                        control={control}
                        setValue={setValue}
                        name={`dailyActivityMonitoringLogDetails.${props.dataIndex}.concern`}
                        readOnly={readOnly}
                        dailyActivityMonitoringId={props.dataItem.id}
                        memberId={props.dataItem.memberId}
                        memberName={props.dataItem.memberName}
                      />
                    </td>
                  ),
                },
              },
              {
                field: "signature",
                title: "Signature of Assisted Member",
                width: "400px",
                editor: "upload",
                editorProps: ({ dataItem }: { dataItem: ActivityRow }) =>
                  buildDamlSignatureEditorProps({
                    dailyActivityMonitoringDetailId: dataItem.id,
                    readOnly,
                    queryClient,
                  }),
              },
            ];

          return (
            <>
              <FormSection title="Header" loading={loading}>
                <FormFieldSet cols={2}>
                  {action === "edit" && (
                    <ControlledInput
                      name="logId"
                      control={control}
                      loading={loading}
                      label="Log ID"
                      readOnly
                    />
                  )}
                  <ControlledInput
                    name="coordinatorName"
                    control={control}
                    loading={loading}
                    label="Coordinator Name"
                    readOnly
                  />
                  <ControlledInput
                    name="dailyWorkSchedule"
                    control={control}
                    loading={loading}
                    label="Daily Work Schedule"
                    readOnly
                  />
                  <ControlledDatePicker
                    name="date"
                    control={control}
                    loading={loading}
                    label="Date"
                    readOnly={dateTimeReadOnly}
                    required
                  />
                  {dateTimeReadOnly ? (
                    <div className="k-form-field">
                      <div className="k-form-field-wrap">
                        <label className="k-form-label">Time</label>
                        <span>
                          {formatDate(getValues().time as Date, "hh:mm a")}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <ControlledTimePicker
                      name="time"
                      control={control}
                      loading={loading}
                      label="Time"
                      format="hh:mm a"
                      required
                    />
                  )}
                  {branches && (
                    <ControlledComboBox
                      name="branchId"
                      control={control}
                      loading={loading}
                      label="Site/ Branch"
                      data={branches}
                      dataItemKey="id"
                      textField="name"
                      readOnly
                    />
                  )}
                  <ControlledInput
                    name="statusName"
                    control={control}
                    loading={loading}
                    label="Status"
                    readOnly
                  />
                </FormFieldSet>
              </FormSection>

              <FormSection title="Activity Table" loading={loading}>
                <ControlledDataGrid
                  fieldArrayName="dailyActivityMonitoringLogDetails"
                  columns={activityColumns}
                  readOnly={readOnly}
                  canAdd={false}
                  hideRowCommandToggle
                  control={control}
                  dataItemKey="id"
                  loading={loading}
                />
              </FormSection>

              <FormActionsBar loading={loading}>
                {readOnly ? (
                  <>
                    {isDraft && isOwner && (
                      <Button
                        type="button"
                        svgIcon={pencilIcon}
                        themeColor="base"
                        onClick={(e) => {
                          e.preventDefault();
                          navigate({
                            to: "/deployment/daily-activity-monitoring-logs/$id/edit",
                            params: { id: id! },
                          });
                        }}
                      >
                        Edit
                      </Button>
                    )}
                    {isSubmitted && (
                      <Button
                        type="button"
                        themeColor="primary"
                        onClick={() => handleFinalize(getValues())}
                      >
                        Finalize
                      </Button>
                    )}
                  </>
                ) : (
                  <>
                    <Button type="submit" svgIcon={saveIcon} themeColor="base">
                      Save
                    </Button>
                    {isDraft && (
                      <>
                        <Button
                          type="button"
                          fillMode="outline"
                          themeColor="error"
                          onClick={() => handleCancel(getValues())}
                        >
                          Cancel
                        </Button>
                        <Button
                          type="button"
                          themeColor="primary"
                          onClick={() => handleSubmitFromEdit(getValues())}
                        >
                          Submit
                        </Button>
                      </>
                    )}
                  </>
                )}
                <Button
                  type="button"
                  fillMode="outline"
                  svgIcon={cancelIcon}
                  themeColor="secondary"
                  onClick={goExit}
                >
                  Exit
                </Button>
              </FormActionsBar>
            </>
          );
        }}
      </Form>
    </ContentLayout>
  );
};

export default DailyActivityMonitoringLogForm;
